/**
 * The upload door and the version rules (piece 9; decisions 0068, 0234, task
 * #227): the blocked-extension list matched on the last extension and the one
 * before it, the 100 MB limit named before the upload starts, the expiry
 * stamped once from the type, the storage key, and which version streams.
 */

/** The seeded executable and script set; `.html` and `.svg` stay allowed because the proxy serves them as attachments. */
export const DEFAULT_BLOCKED_EXTENSIONS: readonly string[] = [
  'exe', 'dll', 'com', 'bat', 'cmd', 'msi', 'msp', 'scr', 'pif', 'cpl', 'msc', 'hta', 'reg', 'lnk', 'inf',
  'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'ws', 'ps1', 'psm1', 'psd1',
  'sh', 'bash', 'zsh', 'csh', 'ksh', 'py', 'pyc', 'rb', 'pl', 'php', 'jar', 'app', 'apk', 'deb', 'rpm', 'iso',
];

export const BLOCKED_EXTENSIONS_SETTING_KEY = 'files.blockedExtensions';

/** The extension that refuses the upload, with its dot, or null: the last extension and the one before it are both checked. */
export function blockedExtension(filename: string, blocked: readonly string[] = DEFAULT_BLOCKED_EXTENSIONS): string | null {
  const parts = filename.toLowerCase().split('.');
  if (parts.length < 2) return null;
  const set = new Set(blocked.map((e) => e.toLowerCase().replace(/^\./, '')));
  const candidates = parts.slice(-2).slice(parts.length === 2 ? 1 : 0);
  for (const ext of candidates.reverse()) if (set.has(ext)) return `.${ext}`;
  return null;
}

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

export function uploadTooLarge(sizeBytes: number): boolean {
  return sizeBytes > MAX_UPLOAD_BYTES;
}

export const UPLOAD_LIMIT_LABEL = '100 MB';

/** Stamped at upload from FileType.defaultExpiryMonths and never recomputed; a type naming no months leaves it null. */
export function expirationFrom(uploadedAt: Date, defaultExpiryMonths: number | null): Date | null {
  if (defaultExpiryMonths === null || defaultExpiryMonths === undefined) return null;
  const d = new Date(Date.UTC(uploadedAt.getUTCFullYear(), uploadedAt.getUTCMonth(), uploadedAt.getUTCDate()));
  d.setUTCMonth(d.getUTCMonth() + defaultExpiryMonths);
  return d;
}

/** One key holds one version: {fileId}/{versionId}. */
export function storageKey(fileId: string, versionId: string): string {
  return `${fileId}/${versionId}`;
}

export type ScanVerdictValue = 'Pending' | 'Clean' | 'Blocked';

export interface VersionRow {
  id: string;
  versionNumber: number;
  scanVerdict: ScanVerdictValue;
  scannedAt: Date | null;
  supersededAt: Date | null;
  deletedAt: Date | null;
  permanentlyDeletedAt: Date | null;
}

/** The version an unqualified read serves: the newest row with neither supersededAt nor deletedAt set, and Clean. */
export function newestServableVersion<V extends VersionRow>(versions: readonly V[]): V | null {
  const live = versions.filter((v) => v.supersededAt === null && v.deletedAt === null && v.permanentlyDeletedAt === null);
  live.sort((a, b) => b.versionNumber - a.versionNumber);
  return live.find((v) => v.scanVerdict === 'Clean') ?? null;
}

/** The newest surviving version whatever its verdict, so the row can show Scanning or Blocked. */
export function newestVersion<V extends VersionRow>(versions: readonly V[]): V | null {
  const live = versions.filter((v) => v.supersededAt === null && v.deletedAt === null);
  live.sort((a, b) => b.versionNumber - a.versionNumber);
  return live[0] ?? null;
}

export type HeldState = 'Scanning' | 'Blocked' | 'Gone';

/** Why a version does not stream: Pending is Scanning, Blocked is Blocked, purged bytes are Gone; null streams. */
export function heldState(version: Pick<VersionRow, 'scanVerdict' | 'permanentlyDeletedAt'>): HeldState | null {
  if (version.permanentlyDeletedAt !== null) return 'Gone';
  if (version.scanVerdict === 'Pending') return 'Scanning';
  if (version.scanVerdict === 'Blocked') return 'Blocked';
  return null;
}

export const QUARANTINE_DAYS = 30;

/** Thirty days from scannedAt on a Blocked version, its own clock and never the trash window's (decision 0234). */
export function quarantineDue(version: Pick<VersionRow, 'scanVerdict' | 'scannedAt' | 'permanentlyDeletedAt'>, now: Date, days = QUARANTINE_DAYS): boolean {
  if (version.scanVerdict !== 'Blocked' || version.scannedAt === null || version.permanentlyDeletedAt !== null) return false;
  return now.getTime() - version.scannedAt.getTime() >= days * 86_400_000;
}

/**
 * 🔨 A named versionId opens the current version alone (decision 0345): a
 * superseded or trashed version is refused naming the current one, and a
 * purged version is the proxy's own Gone state. Null means the version serves.
 */
export function namedVersionProblem<V extends VersionRow>(named: V, versions: readonly V[]): string | null {
  if (named.permanentlyDeletedAt !== null) return null;
  if (named.supersededAt === null && named.deletedAt === null) return null;
  const current = newestServableVersion(versions) ?? newestVersion(versions);
  const state = named.deletedAt !== null ? 'trashed' : 'superseded';
  return `FileVersion ${named.id} is ${state}; the current version is ${current?.id ?? 'none'}`;
}

/** A link created with no expiry takes files.shareLinkExpiryDays from the moment of creation (decision 0345); a day count that is not positive means no default. */
export function defaultShareExpiry(now: Date, days: unknown): Date | null {
  const count = typeof days === 'number' ? days : Number(days);
  if (!Number.isFinite(count) || count <= 0) return null;
  return new Date(now.getTime() + Math.floor(count) * 86_400_000);
}

/** A Confidential File's share link requires a recipient and an expiry by rule, though neither column is NOT NULL. */
export function confidentialShareProblems(file: { confidentialityRoleId: string | null }, link: { recipientPersonId: string | null; expiresAt: Date | null }): string[] {
  if (file.confidentialityRoleId === null) return [];
  const problems: string[] = [];
  if (link.recipientPersonId === null) problems.push('recipientPersonId');
  if (link.expiresAt === null) problems.push('expiresAt');
  return problems;
}
